import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Box from '@mui/material/Box';
import { ThemeProvider, Theme, StyledEngineProvider } from '@mui/material/styles';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PhoneIcon from '@mui/icons-material/Phone';
import { ButtonApp } from 'src/components/Button';
import {
  IResponsibleUser, ICreatioClient, ListOfKeysFilterAllData, IFilterAllData,
} from 'src/typings/filter';
import { getCreatioDeal } from 'src/store/crosstable/selectors';
import { Actions } from 'src/store/requests/filter/actions';
import { Actions as ActionsView2d } from 'src/store/view-2d/actions';
import { Actions as ActionsFilteredData } from 'src/store/filter/filtered-data/actions';
import { Actions as SpinnerActions } from 'src/store/requests/spinner-views/actions';
import { Actions as AppActions } from 'src/store/app/actions';
import { getCrmData, getBindingAppartment, getCurrentLang } from 'src/store/app/selectors';
import { useGetAuthData } from 'src/hooks';
import { getPlacement, patchCreatioDeal, getCreatioResponsibleUsers } from 'src/api';
import { ICreatioDeal } from 'src/store/crosstable/reducer';
import { useConfirm } from 'material-ui-confirm';
import { IApartmentConnectDealWithPlacement } from './index';
import { dealsTheme, useStyles } from './styles';
import { translations } from '../../constants/translations';

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

interface ICreatioDealDetails {
  creatioId: string;
  responsibleUser: IResponsibleUser;
  contact: ICreatioClient;
}

export const CreatioDeal: React.FC<IApartmentConnectDealWithPlacement> = React.memo(({
  placeInfo,
  isSmall,
}) => {
  const styles = useStyles({});
  const confirm = useConfirm();
  const dispatch = useDispatch();
  const crmData = useSelector(getCrmData);
  const creatioDeal = useSelector(getCreatioDeal);
  const currentLang = useSelector(getCurrentLang);
  const isBindingAppartment = useSelector(getBindingAppartment);
  const { getJWT } = useGetAuthData();
  const jwt = getJWT();
  const [currentDeals, setCurrentDeals] = useState<ICreatioDeal[]>([]);
  const [creatioDealDetails, setCreatioDealDetails] = useState<{
    [key: string]: ICreatioDealDetails
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSelected, setIsSelected] = useState(false);

  const fetchGetDealsDetails = async (place: IFilterAllData) => {
    const { data } = await getCreatioResponsibleUsers(jwt, place[ListOfKeysFilterAllData.id]);
    setCreatioDealDetails(data);
  };
  useEffect(() => {
    fetchGetDealsDetails(placeInfo);
  }, [placeInfo]);

  useEffect(() => {
    if (placeInfo[ListOfKeysFilterAllData.creatioOpportunities]) {
      setCurrentDeals(placeInfo[ListOfKeysFilterAllData.creatioOpportunities]);
    } else {
      setCurrentDeals([]);
    }
    setIsLoading(false);
  }, [placeInfo]);

  useEffect(() => {
    let dealIsSelected = false;
    if (currentDeals) {
      currentDeals.forEach((item) => {
        if (item.id === crmData?.dealId) {
          dealIsSelected = true;
        }
      });
    }
    setIsSelected(dealIsSelected);
  }, [currentDeals, crmData]);

  if (!crmData || !creatioDeal) {
    return null;
  }

  const {
    domain, crm,
  } = crmData;

  const { externalId } = creatioDeal;

  const sendPlacementData = useCallback(async (deals: ICreatioDeal[], isRemove: boolean, selectedDeal: ICreatioDeal) => {
    setIsLoading(true);

    const { data: deal } = await patchCreatioDeal(
      jwt,
      selectedDeal.id,
      {
        placements: isRemove ? [] : [`/api/placements/${placeInfo[ListOfKeysFilterAllData.id]}`],
      },
    );

    const { data: newPlacementData } = await getPlacement(
      jwt,
      placeInfo[ListOfKeysFilterAllData.id],
    );

    dispatch(ActionsView2d.setIsRerender());
    if (!isRemove) {
      dispatch(AppActions.setBindingAppartment(true));
      deals.push(deal);
    } else {
      dispatch(AppActions.setBindingAppartment(false));
    }
    dispatch(Actions.updatePlacement({
      data: newPlacementData,
      lang: currentLang,
    }));
    dispatch(ActionsFilteredData.updatePlacement({
      data: newPlacementData,
      lang: currentLang,
    }));
    dispatch(ActionsView2d.updatePlacement({
      data: newPlacementData,
      lang: currentLang,
    }));
    dispatch(SpinnerActions.spinnerDataEditPlacement({
      id: placeInfo[ListOfKeysFilterAllData.id],
      crm,
      data: newPlacementData,
      lang: currentLang,
    }));
    setCurrentDeals(deals);
    setIsLoading(false);
  }, [crmData, placeInfo, jwt, currentDeals]);

  const handleAddDeal = (selectedDeal: ICreatioDeal) => {
    sendPlacementData(currentDeals, false, selectedDeal);
  };

  const handleRemoveDeal = (selectedDeal: any = 0) => {
    const current = selectedDeal;
    const filtered: ICreatioDeal[] = currentDeals.reduce((acc: ICreatioDeal[], item: ICreatioDeal) => {
      if (item.id !== current.id) {
        acc.push(item);
      }
      return acc;
    }, []);

    sendPlacementData(filtered, true, current);
  };

  const handleConnectBtn = () => {
    // e.preventDefault();
    // e.stopPropagation();
    if (isSelected) {
      handleRemoveDeal(creatioDeal);
    } else {
      handleAddDeal(creatioDeal);
    }
  };

  const handleUnbindDeal = useCallback((e, item) => {
    e.preventDefault();
    e.stopPropagation();
    confirm({
      title: `${translations.dialogTitle} №${placeInfo[ListOfKeysFilterAllData.placementNumeration]}`,
      description: `${translations.dialogContent}: ${item.responsibleUser.name}`,
    }).then(() => {
      handleRemoveDeal(item);
    });
  }, [placeInfo]);

  const getBtnText = () => {
    if (!isSelected && !isLoading && isBindingAppartment) return translations.disabledAttach;
    if (isSelected && !isLoading) return `${translations.detachUnitFromDeal} №${externalId}`;
    if (isSelected && isLoading) return `${translations.deleting}...`;
    if (!isSelected && !isLoading) return `${translations.attachUnitToDeal} №${externalId}`;
    if (!isSelected && isLoading) return `${translations.updating}...`;
    return '';
  };

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={dealsTheme}>
        <Box key={placeInfo[ListOfKeysFilterAllData.id]} mt={isSmall ? 1 : 0}>
          { (Number(placeInfo[ListOfKeysFilterAllData.placementCanBeSet])
            || (isSelected && !Number(placeInfo[ListOfKeysFilterAllData.placementCanBeSet]))) && (
              <Box mb={currentDeals.length && !(currentDeals.length === 1 && isSelected) ? 6 : 0}>
                <ButtonApp
                  text={getBtnText()}
                  type="button"
                  color={isSelected ? 'secondary' : 'primary'}
                  onClick={handleConnectBtn}
                  isCapitalize
                  isFullSize
                  disabled={isLoading || (isBindingAppartment && !isSelected)}
                  customClassName={isSmall ? styles.isSmallBtn : ''}
                  variant={isSelected ? 'outlined' : 'contained'}
                />
              </Box>
          )}
          {currentDeals && currentDeals.length && !(currentDeals.length === 1 && isSelected) ? (
            <Box>
              {translations.otherAttachedDeals}
              {currentDeals.map((item: ICreatioDeal) => {
                if (!item || item.id === crmData?.dealId) return null;
                return (
                  <a
                    href={`https://${domain}/0/Nui/ViewModule.aspx#CardModuleV2/OpportunityPageV2/edit/${item.externalId}/`}
                    title={translations.openDealInCRM}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    key={item.id}
                  >
                    <Box
                      width="100%"
                      mt={2}
                      borderRadius="5px"
                      position="relative"
                      className={styles.dealBtn}
                    >
                      <Box fontSize="13px">
                        {`№${item.externalId}`}
                      </Box>
                      {creatioDealDetails && (
                        <>
                          {creatioDealDetails[item.id]?.responsibleUser && (
                          <Box mt={1} fontSize="11px">
                            {`${translations.responsible}: ${creatioDealDetails[item.id]?.responsibleUser.name}`}
                          </Box>
                          )}
                          {creatioDealDetails[item.id]?.contact && (
                          <>
                            <Box mt={1} fontSize="11px">
                              {`${translations.client}: ${creatioDealDetails[item.id]?.contact.name}`}
                            </Box>
                            {creatioDealDetails[item.id]?.contact.phone && (
                              <Box key={creatioDealDetails[item.id]?.contact.phone} mt={1} fontSize="11px" display="flex" alignItems="center">
                                <Box fontSize="15px" lineHeight="15px" mr={1}>
                                  <PhoneIcon fontSize="inherit" />
                                </Box>
                                {creatioDealDetails[item.id]?.contact.phone}
                              </Box>
                            )}
                            {creatioDealDetails[item.id]?.contact.homePhone && (
                              <Box key={creatioDealDetails[item.id]?.contact.homePhone} mt={1} fontSize="11px" display="flex" alignItems="center">
                                <Box fontSize="15px" lineHeight="15px" mr={1}>
                                  <PhoneIcon fontSize="inherit" />
                                </Box>
                                {creatioDealDetails[item.id]?.contact.homePhone}
                              </Box>
                            )}
                            {creatioDealDetails[item.id]?.contact.mobilePhone && (
                              <Box key={creatioDealDetails[item.id]?.contact.mobilePhone} mt={1} fontSize="11px" display="flex" alignItems="center">
                                <Box fontSize="15px" lineHeight="15px" mr={1}>
                                  <PhoneIcon fontSize="inherit" />
                                </Box>
                                {creatioDealDetails[item.id]?.contact.mobilePhone}
                              </Box>
                            )}
                          </>
                          )}
                        </>
                      )}
                      <Box
                        borderRadius="0 5px 5px 0"
                        position="absolute"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        top={0}
                        right={0}
                        bottom={0}
                        className={`${styles.removeDeal} removeBtn`}
                        title={translations.detachUnitFromDeal}
                        onClick={(e) => handleUnbindDeal(e, item)}
                      >
                        <DeleteForeverIcon />
                      </Box>
                    </Box>
                  </a>
                );
              })}
            </Box>
          ) : null}
        </Box>
      </ThemeProvider>
    </StyledEngineProvider>
  );
});
